import type {
  CampaignGroupsQueryResponse,
  CampaignGroupsQueryVariables,
} from '~/api/graphql/queries/campaignGroups.generated';
import type { CampaignGroupEdgeSelectFragment } from '~/api/graphql/fragments/campaignGroup.generated';
import type { InfiniteData } from '@tanstack/react-query';
import type { ICampaignGroup } from '../types';

import { useInfiniteCampaignGroups } from './useInfiniteCampaignGroups';
import { CAMPAIGNS_QUERY_PAGE_SIZE } from '~/constants/campaign';
import { refetchAllPages, useFetchAllPages } from '~/hooks';
import { captureSentryError } from '~/exceptions/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { useCampaignGroupStore } from '../stores';
import { useShopStore } from '~/stores/shop';
import { sortByConditions } from '~/utils';

const campaignGroupsQueryVariables: CampaignGroupsQueryVariables = {
  first: CAMPAIGNS_QUERY_PAGE_SIZE,
};

/** Normalizes one group node, whichever query it came from — the listing or `campaignGroup(id)`. */
export const mappingDataCampaignGroup = (campaignGroup: ICampaignGroup): ICampaignGroup => {
  const campaigns = campaignGroup.campaigns?.map((campaign) => {
    const variants = campaign.variants?.filter(Boolean);
    return {
      ...campaign,
      variants:
        variants &&
        sortByConditions(variants, [
          {
            attr: 'isOrigin',
          },
        ]),
    };
  });

  return {
    ...campaignGroup,
    campaigns,
  } as ICampaignGroup;
};

export const mappingDataCampaignGroups = (
  data: CampaignGroupEdgeSelectFragment[],
): ICampaignGroup[] => {
  if (!data) return [];

  const campaignGroups = data.map((campaignGroup) => campaignGroup.node) as ICampaignGroup[];

  return campaignGroups.map(mappingDataCampaignGroup);
};

const flattenEdges = (
  data: InfiniteData<CampaignGroupsQueryResponse> | undefined,
): CampaignGroupEdgeSelectFragment[] =>
  (data?.pages ?? [])
    .flatMap((page) => page?.campaignGroups?.edges ?? [])
    .filter((edge): edge is CampaignGroupEdgeSelectFragment => !!edge);

const hasMorePages = (data: InfiniteData<CampaignGroupsQueryResponse> | undefined): boolean =>
  !!data?.pages.at(-1)?.campaignGroups?.pageInfo?.hasNextPage;

interface IUseQueryCampaignGroupsResult {
  refetch: () => Promise<void>;
  isFetching: boolean;
}

export const useQueryCampaignGroups = (): IUseQueryCampaignGroupsResult => {
  const shopInfo = useShopStore((state) => state.shopInfo);
  const isQueryByCampaignGroups = useCampaignGroupStore((state) => state.isQueryByCampaignGroups);
  const setCampaignGroups = useCampaignGroupStore((state) => state.setCampaignGroups);
  const setIsQueryByCampaignGroups = useCampaignGroupStore(
    (state) => state.setIsQueryByCampaignGroups,
  );

  const queryClient = useQueryClient();

  const enabled = !!shopInfo?.shopID && !!shopInfo.token && !isQueryByCampaignGroups;

  const { data, refetch, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage, isError } =
    useInfiniteCampaignGroups(campaignGroupsQueryVariables, {
      enabled,
      getNextPageParam: (lastPage) => {
        const pageInfo = lastPage?.campaignGroups?.pageInfo;
        return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined;
      },
      // Groups mounts in many non-Campaigns contexts (reports, orders,
      // make-winner, theme-page-management modals) so we just log to
      // Sentry — navigating away would yank users out of unrelated flows.
      onError: (error: Error) => {
        captureSentryError(error, {
          source: 'useQueryCampaignGroups',
          level: 'warning',
          tags: { experiment: 'Experiment' },
        });
      },
    });

  useFetchAllPages({
    data,
    hasNextPage,
    isFetchingNextPage,
    isError,
    enabled,
    fetchNextPage,
    onAllPagesLoaded: (loaded) => {
      // Shared-store guard: when several consumers mount concurrently,
      // only the first one to fire commits to the store. Subsequent
      // instances early-return so we don't write the same edges N times.
      if (useCampaignGroupStore.getState().isQueryByCampaignGroups) return;

      setCampaignGroups(mappingDataCampaignGroups(flattenEdges(loaded)));
      setIsQueryByCampaignGroups(true);
    },
  });

  // Throws on failure so callers decide recovery (fail-safe / toast /
  // ignore)
  const handleRefetch = async (): Promise<void> => {
    try {
      const fresh = await refetchAllPages({
        queryClient,
        queryKey: useInfiniteCampaignGroups.getKey(campaignGroupsQueryVariables),
        refetch,
        fetchNextPage,
        hasMorePages,
      });

      setCampaignGroups(mappingDataCampaignGroups(flattenEdges(fresh)));
      setIsQueryByCampaignGroups(true);
    } catch (error) {
      captureSentryError(error, {
        source: 'useQueryCampaignGroups.handleRefetch',
        level: 'warning',
        tags: { experiment: 'Experiment' },
      });
      throw error;
    }
  };

  return {
    // `hasNextPage` keeps `isFetching` true across the gap between a page
    // resolving and the next fetchNextPage firing in the effect.
    isFetching: isFetching || (!isError && !!hasNextPage),
    refetch: handleRefetch,
  };
};
